// Utils
import { sentryErrorLog } from "@utils/sentryLog";
import { numberToCurrency, toFixedWithoutRounding } from "@utils/common";
import { BNB, HEDERA } from "@constants/constants";

const xToken = process.env.NEXT_PUBLIC_XTOKEN || "";
const token = process.env.NEXT_PUBLIC_TOKEN || "";

export const getDefiData = async (defiData: any) => {
  const defiItems = defiData
    .map((data: any) => data.items)
    .flat()
    .filter((item: any) => item?.poolKey);

  const [beefyEllipsis, firestoreData, defillamaResult] = await Promise.all([
    getBeefyEllipsisPool(),
    getFirestoreData(),
    getDefillamaData(defiItems),
  ]);

  defillamaResult &&
    Object.keys(defillamaResult).map((defiKey: any) => {
      const defillamaData = { ...defillamaResult[defiKey] };
      defillamaResult[defiKey] = { ...firestoreData[defiKey] };
      defillamaResult[defiKey].apy = firestoreData[defiKey]?.apy
        ? firestoreData[defiKey]?.apy
        : defillamaData?.apy;
      defillamaResult[defiKey].tvl = firestoreData[defiKey]?.tvl
        ? firestoreData[defiKey]?.tvl
        : defillamaData?.tvl;
    });

  return {
    ...beefyEllipsis,
    ...firestoreData,
    ...defillamaResult,
  };
};

const formatDefillamaAPIResponse = (defillamaResponse: any) => {
  return {
    apy: `${toFixedWithoutRounding(defillamaResponse.apy.toString(), 2)}`,
    tvl: numberToCurrency(defillamaResponse.tvlUsd),
  };
};

export const getDefillamaData = async (defiItems: any) => {
  const defillamaResult = await Promise.all(
    defiItems.map(async (item: any) => {
      const key = item.apiKey;
      return fetch(`https://yields.llama.fi/chart/${item?.poolKey}`)
        .then(async (res: any) => {
          if (res?.status === 404) {
            throw new Error("Fetching failed");
          }
          const result = await res.json();

          const apiResult =
            result.data && result.data.length > 0 ? result.data.pop() : null;
          const formattedData = apiResult
            ? formatDefillamaAPIResponse(apiResult)
            : null;
          return { [key]: formattedData };
        })
        .catch((error: any) => {
          sentryErrorLog(error);
          return {
            [key]: null,
          };
        });
    })
  );

  return defillamaResult &&
    defillamaResult?.length > 0 &&
    defillamaResult.every((res) => res !== undefined)
    ? defillamaResult.reduce((acc, curr) => {
        const key = Object.keys(curr)[0];
        acc[key] = curr[key];
        return acc;
      })
    : {};
};

export const getFirestoreData = async () => {
  try {
    const response = await fetch(
      `https://us-central1-stader-landing-ui.cloudfunctions.net/app/defi/${
        process.env.NEXT_PUBLIC_TOKEN?.toLowerCase() === "fantom"
          ? "fantom-v2"
          : process.env.NEXT_PUBLIC_TOKEN?.toLowerCase() || ""
      }`
    );
    const resp = await response.json();
    const formattedResp = formatData(resp);
    return formattedResp;
  } catch (error) {
    sentryErrorLog(error);
    return {
      error,
    };
  }
};

const formatData = (data: any) => {
  if (token !== HEDERA) {
    return data;
  }
  let formattedData: any = {};
  const keys = Object.keys(data);
  keys.forEach((item) => {
    const items = data[item];
    items.forEach((defiItems: any, index: number) => {
      const x = { ...defiItems };
      x.tvl = getFormattedNumber(defiItems.liquidity);
      formattedData[`${item}${index}`] = x;
    });
  });
  return formattedData;
};

const getFormattedNumber = (n: any) => {
  if (typeof n === "string") {
    return `${n}`;
  }

  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    notation: "compact",
    compactDisplay: "short",
  }).format(n)}`;
};

export const getBeefyEllipsisPool = async () => {
  try {
    if (xToken !== BNB) {
      return {};
    }
    const [tvlResponse, apyResponse] = await Promise.all([
      fetch("https://api.beefy.finance/tvl"),
      fetch("https://api.beefy.finance/apy"),
    ]);

    const tvlResp = await tvlResponse.json();
    const apyResp = await apyResponse.json();

    return {
      beefyEllipsis: {
        tvl: numberToCurrency(tvlResp["56"]["ellipsis-bnb-bnbx"]),
        apy: (+(apyResp["ellipsis-bnb-bnbx"] * 100)).toFixed(2).toString(),
        enable: true,
        showTVL: false,
      },
      beefyApeswap: {
        tvl: numberToCurrency(tvlResp["56"]["banana-bnbx-wbnb"]),
        apy: (+(apyResp["banana-bnbx-wbnb"] * 100)).toFixed(2).toString(),
        enable: true,
        showTVL: false,
      },
      beefyPancake: {
        tvl: numberToCurrency(tvlResp["56"]["cake-stable-bnbx-wbnb"]),
        apy: (+(apyResp["cake-stable-bnbx-wbnb"] * 100)).toFixed(2).toString(),
        enable: true,
        showTVL: false,
      },
      beefyThenaBNBXFrax: {
        tvl: numberToCurrency(tvlResp["56"]["thena-bnbx-frax"]),
        apy: (+(apyResp["thena-bnbx-frax"] * 100)).toFixed(2).toString(),
        enable: true,
        showTVL: false,
      },
    };
  } catch (error) {
    sentryErrorLog(error);
    return { error };
  }
};
